import { Skeleton } from '@/components/ui/skeleton';
import { ToggleRow } from '@/modules/settings';
import type { QualityJudgeMode } from '@/types';

interface QualityCheckCardProps {
	mode: QualityJudgeMode;
	onModeChange: (next: QualityJudgeMode) => void;
	isLoading: boolean;
	isError: boolean;
	disabled?: boolean;
}

/**
 * A row for the Automation page's Advanced card. Lets a brand pick how hard a draft is checked before it is sent. On is the faster `light`
 * check; off is the full `strict` check, which is also what every brand has until it chooses.
 */
export function QualityCheckCard({ mode, onModeChange, isLoading, isError, disabled }: QualityCheckCardProps) {
	if (isError) {
		return (
			<div className='rounded-2xl border border-gray-500 bg-white px-4 py-4'>
				<p className='text-[13px] text-red-600'>Failed to load settings.</p>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className='rounded-2xl border border-gray-500 bg-white px-4 py-4 space-y-3'>
				<Skeleton className='h-4 w-48' />
				<Skeleton className='h-4 w-72' />
			</div>
		);
	}

	const isLight = mode === 'light';

	return (
		<ToggleRow
			title='Faster quality check'
			description={
				isLight
					? 'Drafts get a quicker check before they are sent. Turn off for the full check.'
					: 'Drafts get the full check before they are sent. Turn on for a quicker check.'
			}
			checked={isLight}
			disabled={disabled}
			onCheckedChange={(checked) => onModeChange(checked ? 'light' : 'strict')}
		/>
	);
}
